import cx from 'clsx';
import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { ReactComponent as TableSort } from '@assets/svg/ic-tableSort.svg';

import { useGroupFacilitySwitcherActions } from '@components/common/Header/store/useGroupFacilitySwitcherStore';
import TableEmptyRowExtended from '@components/common/TableEmptyRowExtended/TableEmptyRowExtended';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';

import { initialSorting } from '@constants/tableSorting';

import useTranslation from '@hooks/useTranslation';

import type { ILocation } from '@model/structureData';

import { useHardwareOverviewQuery } from '@services/hardware/useHardwareOverviewQuery';
import { useAssetTreeQuery } from '@services/useAssetTreeQuery';

import { useAppActions } from '@store/useAppStore';

import { equipmentTypeNamesList } from '../../constants/equipmentTypeNamesList';
import { equipmentTypesList } from '../../constants/equipmentTypesList';
import { getTableSort } from '../../helpers/getTableSort';
import {
  selectHardwareOverviewSorting,
  useHardwareOverviewActions,
} from '../../store/useHardwareOverviewStore';
import TableHardwareEquipmentCell from '../TableHardwareEquipmentCell/TableHardwareEquipmentCell';
import TooltipHardwareOverview from '../TooltipHardwareOverview/TooltipHardwareOverview';
import TableHardwareSkeleton from './TableHardwareSkeleton';
import style from './style.module.scss';

const HO_CELL_TOOLTIP_ID = 'ho-cell-tooltip-id';

const TableHardware = () => {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { setAppLocationSelectValue } = useAppActions();

  const { data: assetTreeData } = useAssetTreeQuery();
  const { data: hardwareOverviewData, isFetching } = useHardwareOverviewQuery();

  const { setGroupFacilitySwitcher } = useGroupFacilitySwitcherActions();
  const { setSorting } = useHardwareOverviewActions();
  const sorting = selectHardwareOverviewSorting();

  const tableData = getTableSort(hardwareOverviewData?.list, sorting.desc);
  const equipmentTypesTableList =
    isFetching || !tableData?.length ? equipmentTypesList : hardwareOverviewData?.availableEquipmentTypesList;

  const onChangeSorting = (key: string) => {
    if (!sorting.id || sorting.id !== key) {
      setSorting({ id: key, desc: false });
      return;
    }
    const { id, desc } = sorting;
    if (desc) {
      setSorting(initialSorting);
    } else {
      setSorting({ id, desc: true });
    }
  };

  const handleClickRow = (facilityId: number) => {
    const selectedFacility = assetTreeData?.facilities?.find(
      (facility: ILocation) => facility.id === facilityId,
    );

    setGroupFacilitySwitcher('byFacility');
    setAppLocationSelectValue({ ...selectedFacility, filterIds: [selectedFacility?.id] });
    navigate(`/customer/${customerId}/hardware-overview/details`, {
      state: { prevPage: pathname },
    });
  };

  return (
    <div className={style.tableWrapper}>
      <table className={style.hardwareTable} cellSpacing="0" cellPadding="0">
        <thead>
          <tr>
            <th>
              <span>{t('Facility')}</span>
              <TableSort
                className={cx(style.tableSortIcon, { [style.activeSort]: sorting.id === 'facilityName' })}
                data-tooltip-content="facilityName"
                data-tooltip-id="sort-tooltip"
                onClick={() => onChangeSorting('facilityName')}
              />
            </th>
            {equipmentTypesTableList?.map((key) => (
              <th key={uuidv4()}>{t(equipmentTypeNamesList[key as keyof typeof equipmentTypeNamesList])}</th>
            ))}
            <th>{t('Open Items')}</th>
          </tr>
        </thead>
        <tbody className={style.hardwareTableBody}>
          {isFetching ? (
            <TableHardwareSkeleton />
          ) : tableData?.length ? (
            tableData.map((item) => (
              <tr key={item.id} data-qa={'facility-row'} onClick={() => handleClickRow(item.id)}>
                <td data-qa={'facility-name'}>{item.name}</td>
                {hardwareOverviewData?.availableEquipmentTypesList?.map((key) => (
                  <td key={`${item.id}-${key}`}>
                    <TableHardwareEquipmentCell
                      tooltipId={HO_CELL_TOOLTIP_ID}
                      info={item[key as keyof typeof equipmentTypeNamesList]}
                    />
                  </td>
                ))}
                <td data-qa={'active_ai_amount'}>{item.all_ai_count}</td>
              </tr>
            ))
          ) : (
            <TableEmptyRowExtended text={t('Nothing was found.')} />
          )}
        </tbody>
      </table>
      <TooltipSort id="sort-tooltip" sorting={sorting} />
      <TooltipHardwareOverview id={HO_CELL_TOOLTIP_ID} />
    </div>
  );
};

export default TableHardware;
